import React, { useState } from 'react'
import { Button, CircularProgress } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import { Radio } from 'components/forms/Radio'
import Input from 'components/forms/Input'
import { Icon } from 'styles'
import { searchMotusByDot, searchMotusByName } from '../Tabs/Motus/motusApi'
import { Toast } from 'components/shared/Toaster'
import MotusNameDetail from '../Tabs/Motus/MotusNameDetail'
import AuthService from 'tools/AuthService'
import strings from 'strings'

/**
 * Extracts navigable motus data from a DOT search response.
 */
function buildMotusNavState(res, dotNumber, fallbackItem) {
    const primaryLocation = res?.locations?.find(l => l.primaryAddressFlag) || res?.locations?.[0]
    return {
        name: res?.entityName || fallbackItem?.entityName || '',
        dbaName: res?.entityNames?.find(n => n.nameType === 'DBA')?.entityName || fallbackItem?.dbaName || '',
        dotNumber: dotNumber || '',
        address1: primaryLocation?.addressLine1 || fallbackItem?.location?.addressLine1 || '',
        city: primaryLocation?.city || fallbackItem?.location?.city || '',
        state: primaryLocation?.state || fallbackItem?.location?.state || '',
        zip5: primaryLocation?.zipCode || fallbackItem?.location?.zipCode || '',
        country: primaryLocation?.country || fallbackItem?.location?.country || 'USA',
        motusDotSearchUuid: res?.motusDotSearchUuid || null,
        email: res?.emailAddresses?.find(e => e.primaryAddressFlag)?.emailAddress || '',
        officers: res?.entityOfficers || [],
    }
}

const MotusSearchContent = ({ history, creditCosts }) => {
    const [searchType, setSearchType] = useState('dot')
    const [dotInput, setDotInput] = useState('')
    const [nameInput, setNameInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [nameResults, setNameResults] = useState(null)

    const { creditUsageEnabled } = AuthService.getClientFeatures()

    const getCreditCost = () => {
        if (!creditCosts) return 0
        const requestType = searchType === 'dot' ? 'MOTUS_DOT' : 'MOTUS_NAME'
        const costObj = creditCosts.find(cost => cost.requestType === requestType)
        return costObj ? costObj.creditCost : 0
    }

    const navigateToCreateApp = (motusData) => {
        history.push({
            pathname: '/create-applicant',
            search: '?source=motus',
            state: { motusData }
        })
    }

    const handleSearch = async () => {
        setLoading(true)
        try {
            if (searchType === 'dot') {
                if (!dotInput) return Toast({ type: 'error', message: 'Please enter a DOT number.' })
                const res = await searchMotusByDot({ dotNumber: dotInput })
                Toast({ type: 'success', message: 'Motus DOT search completed.' })
                navigateToCreateApp(buildMotusNavState(res, dotInput))
            } else {
                if (!nameInput) return Toast({ type: 'error', message: 'Please enter a business name.' })
                const res = await searchMotusByName({ name: nameInput })
                Toast({ type: 'success', message: 'Motus name search completed.' })
                setNameResults(res)
            }
        } catch (err) {
            console.error('Motus search error:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleNameResultSelect = async (dotNumber, item) => {
        setLoading(true)
        try {
            const res = await searchMotusByDot({ dotNumber })
            Toast({ type: 'success', message: 'Motus DOT search completed.' })
            navigateToCreateApp(buildMotusNavState(res, dotNumber, item))
        } catch (err) {
            console.error('Motus DOT search from name result failed:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleRadioChange = (value) => {
        setSearchType(value)
        setDotInput('')
        setNameInput('')
        setNameResults(null)
    }

    const isSearchDisabled = searchType === 'dot' ? !dotInput : !nameInput

    const creditCostEndIcon = creditUsageEnabled && creditCosts ? (
        <span className="creditCostEndIcon">
            <span className="creditCostValue">{getCreditCost()}</span>
            <Icon folder="icons" fileName="credits.svg" />
        </span>
    ) : undefined

    return (
        <>
            <div className='mb-3'>
                <InfoIcon className="mr-2 mb-1" color="primary" />
                <span>Search using Motus by DOT number or business name.</span>
            </div>

            <div className='searchFieldContainer mb-2' style={{ gridTemplateColumns: '3fr 3fr 1fr' }}>
                <div className='d-flex align-items-end'>
                    <Radio
                        checked={searchType}
                        onChange={handleRadioChange}
                        value="dot"
                        size="small"
                        id="motus-dot-radio"
                    />
                    <Input
                        id="motusDot"
                        label="DOT Number"
                        onChange={(e) => setDotInput(e.target.value)}
                        disabled={searchType !== 'dot'}
                        value={dotInput}
                        type="number"
                    />
                </div>

                <div className='d-flex align-items-end'>
                    <Radio
                        checked={searchType}
                        onChange={handleRadioChange}
                        value="name"
                        size="small"
                        id="motus-name-radio"
                    />
                    <Input
                        id="motusName"
                        label="Business Name"
                        onChange={(e) => setNameInput(e.target.value)}
                        disabled={searchType !== 'name'}
                        value={nameInput}
                    />
                </div>


                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={isSearchDisabled || loading}
                    className="fmcsaSearchButton"
                    endIcon={loading ? undefined : creditCostEndIcon}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : strings.SEARCH}
                </Button>
            </div>

            {searchType === 'name' && nameResults && (
                <div className='tableContainer'>
                    <MotusNameDetail
                        data={nameResults}
                        onDotSearch={handleNameResultSelect}
                        dotSearchLoading={loading}
                        searchedDots={[]}
                    />
                </div>
            )}
        </>
    )
}

export default MotusSearchContent